// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getCarouselProductsVariantChildrenDataView } from "@west/App/Store/Selectors/ProductCarouselSelectors";

interface Parameter {
    carouselId: string;
    parentProductId: string;
    productId: string;
    qtyOrdered?: number;
    unitOfMeasure?: string;
}

type HandlerType = Handler<Parameter>;

export const LoadRealTimePricing: HandlerType = props => {
    if (!props.parameter.unitOfMeasure) {
        return;
    }

    const productOption = getCarouselProductsVariantChildrenDataView(
        props.getState(),
        props.parameter.parentProductId,
        props.parameter.carouselId,
    )?.productInfosByProductId[props.parameter.productId];

    if (props.parameter.unitOfMeasure === productOption?.unitOfMeasure) {
        return;
    }

    const productParameter = {
        productId: props.parameter.productId,
        qtyOrdered: productOption?.qtyOrdered ?? 1,
        unitOfMeasure: props.parameter.unitOfMeasure,
    };

    props.dispatch({
        type: "xProductCarouselVariantChildren/LoadingRealTimePricing",
        parentProductId: props.parameter.parentProductId,
        productId: props.parameter.productId,
        carouselId: props.parameter.carouselId,
    } as any);

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [productParameter],
            onSuccess: realTimePricing => {
                props.dispatch({
                    type: "xProductCarouselVariantChildren/CompleteLoadRealTimePricing",
                    carouselId: props.parameter.carouselId,
                    parentProductId: props.parameter.parentProductId,
                    realTimePricing,
                } as any);
            },
            onError: () => {
                props.dispatch({
                    type: "xProductCarouselVariantChildren/FailedLoadRealTimePricing",
                    carouselId: props.parameter.carouselId,
                    parentProductId: props.parameter.parentProductId,
                } as any);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
};

export const DispatchUpdateCarouselProduct: HandlerType = props => {
    props.dispatch({
        type: "xProductCarouselVariantChildren/UpdateProductInfo",
        carouselId: props.parameter.carouselId,
        parentProductId: props.parameter.parentProductId,
        productId: props.parameter.productId,
        qtyOrdered: props.parameter.qtyOrdered,
        unitOfMeasure: props.parameter.unitOfMeasure,
    } as any);
};

export const chain = [LoadRealTimePricing, DispatchUpdateCarouselProduct];

const updateProductInfoVariantChild = createHandlerChainRunner(chain, "UpdateProductInfoVariantChild");

export default updateProductInfoVariantChild;
